import React, { useState } from 'react';
import TabelaCrud from '../components/TabelaCrud';
import { useCreches } from '../hooks/useCreches';
import api, { getCurrentTenantId } from '../services/api';
import { formatarDataBR } from '../common/data';
import PainelAlertas from './PainelAlertas';
import VistoriasSection from './VistoriasSection';

// O backend (administrativo.service) grava/consulta pela coluna `id_creche` e força o
// tenant via `enforceTenant`. Por isso o campo do formulário precisa se chamar `id_creche`
// (e não `creche_id`, que o backend ignora). Com tenant ativo, a creche é implícita e o
// seletor fica oculto; sem tenant (admin de sistema), o seletor aparece para escolher.
interface FinanceiroInstitucional { id: number; id_creche: number; tipo: string; categoria: string; valor: number; data: string; descricao: string; }
interface InventarioMaterial { id: number; id_creche: number; item: string; categoria: string; quantidade: number; validade: string; }
/**
 * `situacao`, `diasRestantes` e `prazoTexto` vêm CALCULADOS do backend
 * (`administrativo-calculos.ts`). A tela não recalcula data — repetir o cálculo
 * aqui seria uma segunda implementação para o fuso quebrar de novo (PR #82).
 */
interface LicencaAlvara {
  id: number; id_creche: number; orgao: string; documento: string;
  vencimento: string; alerta_dias: number;
  situacao?: 'vencido' | 'vence_em_breve' | 'em_dia' | 'sem_validade';
  diasRestantes?: number | null;
  prazoTexto?: string;
  arquivo?: string | null;
  arquivo_nome?: string | null;
  tem_arquivo?: boolean;
}

const COR_SITUACAO: Record<string, string> = {
  vencido: '#c62828',
  vence_em_breve: '#e65100',
  em_dia: '#2e7d32',
  sem_validade: '#616161',
};

const ICONE_SITUACAO: Record<string, string> = {
  vencido: '🔴', vence_em_breve: '🟠', em_dia: '🟢', sem_validade: '⚪',
};

/** Lê o arquivo como data URI — o alvará vai para o banco, não para disco. */
function lerArquivo(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => reject(new Error('Não foi possível ler o arquivo.'));
    r.readAsDataURL(file);
  });
}
interface RelatorioRegulatorio { id: number; id_creche: number; orgao: string; tipo: string; periodo: string; arquivo_url: string; }

export default function AdministrativoPage() {
  const { options } = useCreches();
  // Com tenant ativo, o backend força id_creche = tenant logado — não exigimos seleção.
  const tenantScoped = Boolean(getCurrentTenantId());
  const classeCampoCreche = tenantScoped ? 'campo-tenant-oculto' : undefined;
  // O painel do topo é servidor-calculado; mexer em vistoria muda o que ele mostra.
  const [versaoPainel, setVersaoPainel] = useState(0);

  return (
    <div className="stack-cadastros">
      <PainelAlertas recarregarEm={versaoPainel} />
      <TabelaCrud<FinanceiroInstitucional>
        titulo="Administrativo — Controle Financeiro da Instituição"
        endpoint="/administrativo/financeiro"
        colunas={[{ chave: 'id', label: 'ID' }, { chave: 'tipo', label: 'Tipo' }, { chave: 'categoria', label: 'Categoria' }, { chave: 'valor', label: 'Valor' }, { chave: 'data', label: 'Data' }]}
        itemVazio={{ id_creche: 0, tipo: 'receita', categoria: '', valor: 0, data: '', descricao: '' }}
        validateItem={(i) => {
          if (!tenantScoped && (!i.id_creche || Number(i.id_creche) <= 0)) return 'Selecione a Creche.';
          return !i.categoria || !i.valor || !i.data ? 'Preencha os campos financeiros obrigatórios.' : null;
        }}
        renderFormulario={(i, onChange) => (
          <>
            <div className="linha-campos linha-3">
              <label className={classeCampoCreche}>Creche<select value={i.id_creche ?? 0} onChange={(e) => onChange('id_creche', Number(e.target.value))}><option value={0}>Selecione...</option>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
              <label>Tipo<select value={i.tipo ?? 'receita'} onChange={(e) => onChange('tipo', e.target.value)}><option value="receita">Receita</option><option value="despesa">Despesa</option><option value="salario">Salário</option></select></label>
              <label>Categoria<input value={i.categoria ?? ''} onChange={(e) => onChange('categoria', e.target.value)} /></label>
            </div>
            <div className="linha-campos linha-2">
              <label>Valor<input type="number" step="0.01" value={i.valor ?? 0} onChange={(e) => onChange('valor', Number(e.target.value))} /></label>
              <label>Data<input type="date" value={i.data ?? ''} onChange={(e) => onChange('data', e.target.value)} /></label>
            </div>
            <label>Descrição<textarea rows={2} value={i.descricao ?? ''} onChange={(e) => onChange('descricao', e.target.value)} /></label>
          </>
        )}
      />

      <TabelaCrud<InventarioMaterial>
        titulo="Administrativo — Inventário de Materiais"
        endpoint="/administrativo/inventario"
        colunas={[{ chave: 'id', label: 'ID' }, { chave: 'item', label: 'Item' }, { chave: 'categoria', label: 'Categoria' }, { chave: 'quantidade', label: 'Quantidade' }]}
        itemVazio={{ id_creche: 0, item: '', categoria: '', quantidade: 0, validade: '' }}
        validateItem={(i) => {
          if (!tenantScoped && (!i.id_creche || Number(i.id_creche) <= 0)) return 'Selecione a Creche.';
          return !i.item || !i.categoria || Number(i.quantidade) <= 0 ? 'Preencha os campos obrigatórios do inventário.' : null;
        }}
        renderFormulario={(i, onChange) => (
          <>
            <div className="linha-campos linha-3">
              <label className={classeCampoCreche}>Creche<select value={i.id_creche ?? 0} onChange={(e) => onChange('id_creche', Number(e.target.value))}><option value={0}>Selecione...</option>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
              <label>Item<input value={i.item ?? ''} onChange={(e) => onChange('item', e.target.value)} /></label>
              <label>Categoria<input value={i.categoria ?? ''} onChange={(e) => onChange('categoria', e.target.value)} /></label>
            </div>
            <div className="linha-campos linha-2">
              <label>Quantidade<input type="number" value={i.quantidade ?? 0} onChange={(e) => onChange('quantidade', Number(e.target.value))} /></label>
              <label>Validade<input type="date" value={i.validade ?? ''} onChange={(e) => onChange('validade', e.target.value)} /></label>
            </div>
          </>
        )}
      />

      <TabelaCrud<LicencaAlvara>
        titulo="Administrativo — Licenças e Alvarás"
        endpoint="/administrativo/licencas"
        // `arquivo` não vem na listagem (MEDIUMTEXT); a edição busca o registro cheio.
        recarregarAoEditar
        colunas={[
          { chave: 'orgao', label: 'Órgão' },
          { chave: 'documento', label: 'Documento' },
          { chave: 'vencimento', label: 'Vencimento', render: (l) => formatarDataBR(l.vencimento) },
          {
            chave: 'situacao',
            label: 'Situação',
            render: (l) => (
              <span style={{ color: COR_SITUACAO[l.situacao || 'sem_validade'], fontWeight: 600 }}>
                {ICONE_SITUACAO[l.situacao || 'sem_validade']} {l.prazoTexto || '—'}
              </span>
            ),
          },
          {
            chave: 'arquivo_nome',
            label: 'Documento anexado',
            render: (l) => (l.arquivo_nome
              ? <a href={`#`} onClick={async (e) => {
                  e.preventDefault();
                  try {
                    const { data } = await api.get(`/administrativo/licencas/${l.id}/arquivo`);
                    // `window.open` com data URI é bloqueado por vários navegadores;
                    // âncora com download passa e ainda preserva o nome do arquivo.
                    const a = document.createElement('a');
                    a.href = data.arquivo;
                    a.download = data.arquivo_nome;
                    a.click();
                  } catch {
                    window.alert('Não foi possível baixar o documento.');
                  }
                }}>⬇ {l.arquivo_nome}</a>
              : <span style={{ color: '#bbb' }}>—</span>),
          },
        ]}
        itemVazio={{ id_creche: 0, orgao: '', documento: '', vencimento: '', alerta_dias: 30 }}
        validateItem={(i) => {
          if (!tenantScoped && (!i.id_creche || Number(i.id_creche) <= 0)) return 'Selecione a Creche.';
          if (!i.orgao || !i.documento || !i.vencimento) return 'Preencha os campos obrigatórios de licença.';
          const dias = Number(i.alerta_dias);
          // Mesmo limite do backend, dito aqui para o gestor não descobrir no erro 400.
          if (!Number.isFinite(dias) || dias <= 0 || dias > 365) return 'O alerta deve ser um número de dias entre 1 e 365.';
          return null;
        }}
        renderFormulario={(i, onChange) => (
          <>
            <div className="linha-campos linha-2">
              <label className={classeCampoCreche}>Creche<select value={i.id_creche ?? 0} onChange={(e) => onChange('id_creche', Number(e.target.value))}><option value={0}>Selecione...</option>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
              <label>Órgão emissor<input value={i.orgao ?? ''} onChange={(e) => onChange('orgao', e.target.value)} /></label>
            </div>
            <label>Documento<input value={i.documento ?? ''} onChange={(e) => onChange('documento', e.target.value)} /></label>
            <div className="linha-campos linha-2">
              <label>Vencimento<input type="date" value={i.vencimento ?? ''} onChange={(e) => onChange('vencimento', e.target.value)} /></label>
              <label>Avisar com quantos dias de antecedência
                <input type="number" min={1} max={365} value={i.alerta_dias ?? 30} onChange={(e) => onChange('alerta_dias', Number(e.target.value))} />
              </label>
            </div>
            <label>Arquivo do documento (PDF ou imagem)
              <input
                type="file"
                accept="application/pdf,image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  // MEDIUMTEXT comporta ~16MB, e base64 infla ~33%. Recusar aqui
                  // dá um motivo legível em vez de um erro de banco no save.
                  if (file.size > 8 * 1024 * 1024) {
                    window.alert('O arquivo precisa ter no máximo 8 MB.');
                    e.target.value = '';
                    return;
                  }
                  onChange('arquivo', await lerArquivo(file));
                  onChange('arquivo_nome', file.name);
                }}
              />
            </label>
            {i.arquivo_nome && (
              <p style={{ fontSize: 12, color: '#666', margin: '4px 0' }}>
                Anexado: <strong>{i.arquivo_nome}</strong>{' '}
                <button type="button" onClick={() => { onChange('arquivo', null); onChange('arquivo_nome', null); }}>
                  remover
                </button>
              </p>
            )}
          </>
        )}
      />

      <VistoriasSection aoMudar={() => setVersaoPainel((v) => v + 1)} />

      <TabelaCrud<RelatorioRegulatorio>
        titulo="Administrativo — Relatórios Regulatórios"
        endpoint="/administrativo/relatorios"
        colunas={[{ chave: 'id', label: 'ID' }, { chave: 'orgao', label: 'Órgão' }, { chave: 'tipo', label: 'Tipo' }, { chave: 'periodo', label: 'Período' }]}
        itemVazio={{ id_creche: 0, orgao: 'MEC', tipo: '', periodo: '', arquivo_url: '' }}
        validateItem={(i) => {
          if (!tenantScoped && (!i.id_creche || Number(i.id_creche) <= 0)) return 'Selecione a Creche.';
          return !i.orgao || !i.tipo || !i.periodo ? 'Preencha os campos obrigatórios do relatório regulatório.' : null;
        }}
        renderFormulario={(i, onChange) => (
          <>
            <div className="linha-campos linha-3">
              <label className={classeCampoCreche}>Creche<select value={i.id_creche ?? 0} onChange={(e) => onChange('id_creche', Number(e.target.value))}><option value={0}>Selecione...</option>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
              <label>Órgão<select value={i.orgao ?? 'MEC'} onChange={(e) => onChange('orgao', e.target.value)}><option value="MEC">MEC</option><option value="CMDCA">CMDCA</option><option value="CREAS">CREAS</option><option value="Vigilancia Sanitaria">Vigilância Sanitária</option></select></label>
              <label>Tipo<input value={i.tipo ?? ''} onChange={(e) => onChange('tipo', e.target.value)} /></label>
            </div>
            <div className="linha-campos linha-2">
              <label>Período<input value={i.periodo ?? ''} onChange={(e) => onChange('periodo', e.target.value)} /></label>
              <label>Arquivo/URL<input value={i.arquivo_url ?? ''} onChange={(e) => onChange('arquivo_url', e.target.value)} /></label>
            </div>
          </>
        )}
      />
    </div>
  );
}

